
import * as XLSX from "xlsx";

const safe = (v) => (v === null || v === undefined ? "" : String(v));

const toNumber = (v, def = 0) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : def;
};

// ✅ TAXE: 20 => 0.20 | 0.2 => 0.2 | "20%" => 0.2
const parseTaxRate = (raw) => {
  if (raw === null || raw === undefined || raw === "") return 0;

  if (typeof raw === "string") {
    const s = raw.replace(",", ".").replace("%", "").trim();
    const n = Number(s);
    if (!Number.isFinite(n)) return 0;
    return n > 1 ? n / 100 : n;
  }

  const n = Number(raw);
  if (!Number.isFinite(n)) return 0;
  return n > 1 ? n / 100 : n;
};

export const generateMaterielReparationsExcel = ({ context, rows }) => {
  const ctx = context || {};
  const rowsSafe = Array.isArray(rows) ? rows : [];

  const generatedAt = ctx?.generatedAt ? new Date(ctx.generatedAt) : new Date();
  const generatedAtStr = generatedAt.toLocaleString("fr-FR");

  const userName = safe(ctx?.userName);
  const materiel = ctx?.materiel || {};
  const chantierName = safe(materiel?.chantier_actuel || "");

  // =========================
  // 1) Calcul des lignes (avec qte *)
  // =========================
  const computed = rowsSafe.map((r) => {
    const prix = toNumber(r.prix ?? r.PRIX, 0);
    const taxeRate = parseTaxRate(r.taxe ?? r.TAXE);
    const qte = toNumber(r.qte ?? r.QTE ?? r.QTE_INIT, 0);

    const htUnit = prix;
    const tvaUnit = prix * taxeRate;
    const ttcUnit = prix + tvaUnit;

    // ✅ Si déjà calculé côté page, on prend
    const htTotal = (r._calc_ht_total !== undefined) ? toNumber(r._calc_ht_total, 0) : (htUnit * qte);
    const ttcTotal = (r._calc_ttc_total !== undefined) ? toNumber(r._calc_ttc_total, 0) : (ttcUnit * qte);

    return {
      date: safe(r.created_at ?? r.createdAt ?? r.DATE_BDC ?? r.date ?? ""),
      idbdc: safe(r.idbdc ?? r.IDBDC ?? ""),
      fournisseur: safe(r.fournisseur_libelle ?? r.FOURNISSEUR_LIBELLE ?? ""),
      code: safe(r.code_produit ?? r.CODE_PRODUIT ?? ""),
      designation: safe(r.designation ?? r.DESIGNATION ?? ""),
      qte,
      unite: safe(r.unite ?? r.UNITE ?? ""),
      ht_unit: htUnit,
      tva_percent: taxeRate * 100,
      ttc_unit: ttcUnit,
      ht_total: htTotal,
      ttc_total: ttcTotal
    };
  });

  const totalHT = computed.reduce((acc, r) => acc + (Number(r.ht_total) || 0), 0);
  const totalTTC = computed.reduce((acc, r) => acc + (Number(r.ttc_total) || 0), 0);
  const totalTVA = totalTTC - totalHT;

  // =========================
  // 2) Construction de la feuille (AOA)
  // =========================
  const aoa = [];

  // Titre
  aoa.push(["Historique Réparations – Matériel"]);
  aoa.push([]);

  // Infos
  aoa.push(["Généré le", generatedAtStr]);
  aoa.push(["Utilisateur", userName]);
  aoa.push([]);

  aoa.push(["ID Engin", safe(materiel?.id)]);
  aoa.push(["Matricule", safe(materiel?.matricule)]);
  aoa.push(["Type", safe(materiel?.type)]);
  aoa.push(["Société", safe(materiel?.societe)]);
  aoa.push(["Chantier actuel", chantierName]);
  aoa.push([]);

  // En-têtes table
  aoa.push([
    "Date",
    "IDBDC",
    "Fournisseur",
    "Code",
    "Désignation",
    "Qté",
    "Unité",
    "HT (unit)",
    "TVA %",
    "TTC (unit)",
    "Total HT",
    "Total TTC"
  ]);

  // Lignes
  computed.forEach((r) => {
    aoa.push([
      r.date,
      r.idbdc,
      r.fournisseur,
      r.code,
      r.designation,
      r.qte,
      r.unite,
      r.ht_unit,
      r.tva_percent,
      r.ttc_unit,
      r.ht_total,
      r.ttc_total
    ]);
  });

  // Totaux
  aoa.push([]);
  aoa.push(["Synthèse"]);
  aoa.push(["Nombre de lignes", computed.length]);
  aoa.push(["Total HT", totalHT]);
  aoa.push(["Total TVA (montant)", totalTVA]);
  aoa.push(["Total TTC", totalTTC]);

  const ws = XLSX.utils.aoa_to_sheet(aoa);

  // ✅ Formats nombres (approximatif: colonnes H à L sur les lignes data)
  // On garde simple pour compatibilité : Excel interprète les nombres comme nombres si on met Number.
  // (Les cellules sont déjà en Number pour ht_unit, etc.)

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Historique");

  const matricule = safe(materiel?.matricule) || "SANS_MATRICULE";
  const fileSafeDate = generatedAtStr.replace(/[^\dA-Za-z-_]/g, "_");
  const filename = `historique_reparations_${matricule}_${fileSafeDate}.xlsx`;

  XLSX.writeFile(wb, filename);
};
